import { alpha } from '@mui/material/styles';
import { Button, SvgIcon, Typography, Box } from '@mui/material';
import UploadIcon from 'assets/icons/upload.svg';
import Loading from 'components/common/Loading';
import { DropzoneOrDivider } from 'components/common/DropzoneBox';
import { bool, func, string, node } from 'prop-types';
import React from 'react';
import Dropzone from 'react-dropzone';
import { I18n } from 'utils/i18n';
import toDropzoneAccept from 'utils/toDropzoneAccept';

const DropzoneBox = ({
  onDrop,
  loading,
  error,
  accept = 'application/pdf',
  showUpload = true,
  children = null,
}) => {
  return (
    <Box
      sx={theme => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        minHeight: 210,
        width: '100%',
        mt: 2,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: theme.palette.primary.ultraLight,
        boxSizing: 'border-box',
      })}
    >
      {loading ? (
        <Loading size={80} />
      ) : (
        <Dropzone accept={toDropzoneAccept(accept)} onDrop={onDrop} multiple={false}>
          {({ getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open }) => (
            <Box
              {...getRootProps()}
              sx={theme => ({
                borderRadius: '4px',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                color: theme.palette.text.primary,
                cursor: 'pointer',
                outlineColor: theme.palette.primary.main,
                width: '100%',
                height: '100%',
                border: `2px dashed ${theme.palette.divider}`,
                backgroundColor: 'transparent',
                ...(isDragAccept && {
                  background: alpha(theme.palette.primary.main, 0.06),
                  borderColor: alpha(theme.palette.primary.main, 0.45),
                  borderStyle: 'dashed',
                }),
                ...(isDragReject && {
                  background: theme.palette.dangerBack,
                  borderColor: theme.palette.danger,
                }),
              })}
            >
              <input {...getInputProps()} />
              {isDragAccept && <p>{I18n.t('AcknowledgementCreate.Drop the file to upload')}</p>}
              {isDragReject && <p>{I18n.t('AcknowledgementCreate.File type not accepted')}</p>}
              {!isDragActive && (
                <>
                  {children && (
                    <>
                      <Box sx={{ width: '100%' }}>{children}</Box>
                      {showUpload && <DropzoneOrDivider />}
                    </>
                  )}

                  {showUpload && (
                    <>
                      <SvgIcon sx={{ color: 'text.secondary', fontSize: '40px' }}>
                        <UploadIcon />
                      </SvgIcon>
                      <Typography
                        variant="body1"
                        align="center"
                        sx={{
                          px: 1,
                          mb: 0.5,
                          fontWeight: 400,
                        }}
                      >
                        {I18n.t('AcknowledgementCreate.Drag & drop file here')}
                      </Typography>
                      <DropzoneOrDivider />
                      <Button
                        type="button"
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ textTransform: 'none' }}
                        onClick={e => {
                          e.stopPropagation();
                          open();
                        }}
                      >
                        {I18n.t('AcknowledgementCreate.Browse file')}
                      </Button>
                    </>
                  )}
                </>
              )}
              {error && (
                <Typography color="error" align="center">
                  {I18n.t('Something went wrong with sending data')}
                </Typography>
              )}
            </Box>
          )}
        </Dropzone>
      )}
    </Box>
  );
};

DropzoneBox.propTypes = {
  onDrop: func.isRequired,
  loading: bool.isRequired,
  error: bool.isRequired,
  accept: string,
  children: node,
  showUpload: bool,
};

export default DropzoneBox;
